import { logoutAction } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { setCurrentURL } from '@Redux/UtilsSlice/UtilsSlice'
import routes from '@Constants/Routes'

/**
 * Machine-readable reasons a session ended, mirroring the backend's `data.code`.
 */
export const SESSION_CODES = {
	IDLE_EXPIRED: 'SESSION_IDLE_EXPIRED',
	REVOKED: 'SESSION_REVOKED',
	SIGNED_IN_ELSEWHERE: 'SESSION_SIGNED_IN_ELSEWHERE',
	DEVICE_REVOKED: 'DEVICE_REVOKED',
	/**
	 * Signed in, but the browser has not completed its device challenge.
	 *
	 * Deliberately NOT terminal: the session is live and valid, the device just
	 * is not trusted yet. Signing them out would destroy the very session the
	 * verification endpoint needs. Handled below by routing to the challenge.
	 */
	DEVICE_UNVERIFIED: 'DEVICE_UNVERIFIED',
	PASSWORD_CHANGED: 'PASSWORD_CHANGED',
} as const

// No DEVICE_LIMIT here. The backend enforces its device cap by evicting the
// least recently used device, not by refusing the login, so there is no
// at-limit response to handle.

/**
 * Not every 401 means "your session died".
 *
 * A wrong password at login is a 401. An unauthenticated /auth/me on a cold
 * boot is a 401. Treating those as terminal would redirect the user away from
 * the login form they are currently using, and overwrite the return URL they
 * were heading for — a login loop.
 */
const TERMINAL_CODES: string[] = Object.values(SESSION_CODES).filter(code => code !== SESSION_CODES.DEVICE_UNVERIFIED)

/**
 * Fire at most once per navigation.
 *
 * A single page can have a dozen queries in flight; without this the student
 * would get a dozen redirects and a dozen snackbars for one expiry.
 */
let handledForPath: string | null = null

/**
 * Called after a successful sign-in.
 *
 * The guard keys on the current path, which covers a burst of parallel 401s but
 * not a second expiry on the SAME path after signing back in — that one was
 * swallowed, leaving the user on a page whose every request 401s with no
 * redirect. Signing in is the unambiguous "that expiry is dealt with" moment.
 */
export const resetUnauthorizedGuard = () => {
	handledForPath = null
}

const messageFor = (code: string | undefined): string => {
	switch (code) {
		case SESSION_CODES.SIGNED_IN_ELSEWHERE:
			return 'You were signed out because your account was used on another device.'
		case SESSION_CODES.DEVICE_REVOKED:
			return 'This device was signed out. Please sign in again.'
		case SESSION_CODES.DEVICE_UNVERIFIED:
			return 'Verify this device to continue.'
		case SESSION_CODES.PASSWORD_CHANGED:
			return 'Your password was changed, so you have been signed out.'
		case SESSION_CODES.REVOKED:
			return 'Your session was ended. Please sign in again.'
		default:
			return 'Your session expired. Please sign in again.'
	}
}

/**
 * Pull the session code out of either transport's error shape.
 *
 * RTK Query hands us a FetchBaseQueryError — `{ status, data }` — so the body
 * is at `error.data`. Axios hands us an AxiosError, where the body is one level
 * deeper at `error.response.data`. Reading only the first shape meant every
 * raw-axios 401 resolved to `undefined` and took the "not a session death"
 * exit, which is most of why the axios path was doing nothing at all.
 */
const codeFrom = (error: unknown): string | undefined => {
	const e = error as any
	return (e?.data?.data?.code ?? e?.response?.data?.data?.code) as string | undefined
}

/**
 * @returns true when this handled the error (and has navigated or will), false
 *   when the 401 is an ordinary one the caller should surface itself. Callers
 *   MUST NOT swallow the error on a false return — that is what silently removed
 *   the "wrong code" feedback from the OTP forms.
 */
export const handleUnauthorized = (dispatch: (action: any) => void, error: unknown): boolean => {
	const code = codeFrom(error)
	const path = `${window.location.pathname}${window.location.search}`

	// The session is fine; the DEVICE is not verified yet. Everything except the
	// challenge endpoints returns this, so without handling it the app renders
	// as signed in while every other request fails. Route to the challenge and
	// do NOT clear auth: the verification call needs this session.
	if (code === SESSION_CODES.DEVICE_UNVERIFIED) {
		if (window.location.pathname === routes.verifyDevice) return true
		// Deduped BEFORE navigating. `window.location.assign` does not update
		// `pathname` synchronously, so without this every one of a page's
		// in-flight queries passed the check above and pushed its own history
		// entry — a dozen identical /verify-device entries to walk back through.
		if (handledForPath === path) return true
		handledForPath = path

		if (!path.startsWith(routes.login)) dispatch(setCurrentURL(path))
		window.location.assign(routes.verifyDevice)
		return true
	}

	// Not a session death — e.g. wrong credentials, or a boot-time /auth/me on a
	// visitor who was never signed in. Let the caller render it.
	//
	// The `!code` half is load-bearing: `code && ...` short-circuits to false
	// when there is no code at all, so a plain 401 would fall THROUGH into the
	// terminal path below and redirect every anonymous visitor off the landing
	// page.
	if (!code || !TERMINAL_CODES.includes(code)) return false

	// Already at the destination — clear state, but do NOT navigate.
	//
	// SessionGuard clears the dead cookie on this very response, so the next
	// boot is anonymous and cannot re-enter this branch; the guard is here for
	// the cases where that clear does not land (a proxy dropping Set-Cookie, a
	// CORS failure). Without it those degrade into an assign loop that leaves
	// the login form unreachable rather than into one wasted round trip.
	//
	// The LOCAL logoutAction is deliberate, not an oversight: a terminal code
	// means the server already revoked this session, and /auth/logout sits
	// behind the `auth` middleware — so calling it would only 401 again.
	if (window.location.pathname === routes.login) {
		dispatch(logoutAction())
		return true
	}

	if (handledForPath === path) return true
	handledForPath = path

	// Capture where they were so login can return them there. Mirrored into
	// sessionStorage by the reducer, because the navigation below rebuilds the
	// store from scratch.
	if (path && !path.startsWith(routes.login)) {
		dispatch(setCurrentURL(path))
	}

	dispatch(logoutAction())

	// Go to login, not home. Landing on the marketing page with no explanation
	// is the worst possible answer to "why am I suddenly logged out".
	const reason = encodeURIComponent(messageFor(code))
	window.location.assign(`${routes.login}?reason=${reason}`)
	return true
}
